<template>
  <OrganizationChartSkeleton
    v-if="loading"
    :message="loadingMessage"
  />
  <OrgChart
    v-else
    :key="chartKey"
    ref="orgChartRef"
    :data 
    :minimap="false"
    class="organization-chart"
  >
    <template #node="{ item, children, childrenExpanded, toggleChildren }">
      <OrganizationChartNode
        :visible-fields
        :item
        :children
        :children-expanded
        :toggle-children
        :value-formatter
        :amount-formatter
      />
    </template>
    <template #noData>
      <UILayoutNoData
        hide-question-icon
        inline
      />
    </template>
  </OrgChart>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watchEffect } from 'vue'
import { OrgChart } from '@moonwane/vue3-org-chart'
import '@moonwane/vue3-org-chart/dist/vue3-org-chart.css'

import type { OrganizationChartNode as OrganizationChartNodeType } from './utils/types'

import { ORGANIZATION_CHART_FIELDS } from './utils/const'

import OrganizationChartNode from './components/OrganizationChartNode.vue'
import OrganizationChartSkeleton from './OrganizationChartSkeleton.vue'
import { UILayoutNoData } from '@ui/core'

type Props = {
  data: OrganizationChartNodeType[]
  loading?: boolean
  loadingMessage?: string
  hiddenFields?: string[]
  valueFormatter?: (value: number) => string
  amountFormatter?: (value: number) => string
}

type Emits = {
  'ready': [instance: typeof OrgChart.prototype]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({ inheritAttrs: false })

const orgChartRef = useTemplateRef('orgChartRef')

const visibleFields = computed(() => ORGANIZATION_CHART_FIELDS?.filter(field => !props.hiddenFields?.includes(field)))

const chartKey = computed(() => JSON.stringify(props.data))

watchEffect(() => {
  if (!orgChartRef.value) return
  emit('ready', orgChartRef.value)
})
</script>

<style>
.organization-chart {
  @apply flex-auto;

  .vue3-org-chart-container {
    @apply h-full!;
  }

  .vue3-org-chart-node-container {
    &:not(:has(.isParentNode)) {
      @apply block;

      .vue3-org-chart-node-element-horizontal-line {
        @apply hidden;
      }

      .vue3-org-chart-node-element {
        @apply mb-0!;
      }
    }
  }
}
</style>
